import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import DownloadImage from './DownloadImage';
import { downloadPerfectImage } from './NewDownloadUtils';

const { FiDownload, FiCheck, FiLoader } = FiIcons;

/**
 * DownloadManager - 下載管理組件
 * 修復：移除未使用的 quote prop；縮短等待時間至 1500ms
 */
const DownloadManager = ({
  language,
  template,
  calculatedDays,
  relationshipLabel,
  detailedTime,
  anniversaryDate,
  anniversaryType,
  isDownloading,
  downloadSuccess,
  onDownloadStart,
  onDownloadComplete
}) => {
  const [showDownloadCanvas, setShowDownloadCanvas] = useState(false);

  const handleDownload = async () => {
    try {
      onDownloadStart();
      setShowDownloadCanvas(true);
      // 等待 DOM 掛載後再截圖（配合 NewDownloadUtils 內部的 1200ms，總計約 2.7s）
      await new Promise(resolve => setTimeout(resolve, 1500));
      await downloadPerfectImage({
        template,
        calculatedDays,
        relationshipLabel,
        anniversaryDate,
        anniversaryType,
        language
      });
      onDownloadComplete(true);
    } catch (error) {
      console.error('下載失敗:', error);
      onDownloadComplete(false);
    } finally {
      setShowDownloadCanvas(false);
    }
  };

  const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6">
      <motion.button
        onClick={handleDownload}
        disabled={isDownloading}
        className={`
          w-full px-6 py-4 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-3
          ${isDownloading
            ? 'bg-gray-400 cursor-not-allowed'
            : downloadSuccess
              ? 'bg-green-500 hover:bg-green-600'
              : 'bg-love-500 hover:bg-love-600 active:scale-95'
          }
        `}
        whileTap={!isDownloading ? { scale: 0.98 } : {}}
      >
        {isDownloading ? (
          <>
            <SafeIcon icon={FiLoader} className="animate-spin" />
            {language === 'zh' ? '生成中...' : 'Generating...'}
          </>
        ) : downloadSuccess ? (
          <>
            <SafeIcon icon={FiCheck} />
            {language === 'zh' ? '下載成功！' : 'Downloaded!'}
          </>
        ) : (
          <>
            <SafeIcon icon={FiDownload} />
            {language === 'zh' ? '下載圖片' : 'Download Image'}
          </>
        )}
      </motion.button>

      <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
        {isIOSDevice ? (
          language === 'zh'
            ? '點擊下載圖片，選擇「儲存影像」保存至相簿'
            : 'Tap download, then select "Save to Photos"'
        ) : (
          language === 'zh'
            ? '點擊下載按鈕，圖片將自動保存到您的設備'
            : 'Click download to save the image to your device'
        )}
      </div>

      {/* 隱藏的下載畫布 */}
      {showDownloadCanvas && (
        <div
          id="perfect-download-canvas"
          style={{
            position: 'fixed',
            left: '-9999px',
            top: '0px',
            width: '1080px',
            height: '1350px',
            overflow: 'visible',
            zIndex: -9999,
            pointerEvents: 'none'
          }}
        >
          <DownloadImage
            template={template}
            language={language}
            calculatedDays={calculatedDays}
            relationshipLabel={relationshipLabel}
            detailedTime={detailedTime}
            anniversaryDate={anniversaryDate}
            anniversaryType={anniversaryType}
          />
        </div>
      )}
    </div>
  );
};

export default DownloadManager;